import moment from 'moment'
import { funnelStageOptions, barrierOptions } from './constants'

export const defaultFilters = () => ({
  id: null,
  country: null,
  country_id: null,
  customer: null,
  journey: null,
  channels: [],
  funnelFrom: '',
  funnelTo: 'any',
  stageEnteredFrom: '',
  stageEnteredTo: '',
  conversationStageOption: null,
  gender: [],
  minAge: '',
  maxAge: '',
  // null, not '' — vue-treeselect treats '' as a real (but unresolvable) selected id and
  // renders "(unknown)"; null is what it recognizes as "nothing selected".
  stateProvince: null,
  cityLga: null,
  recencyFrom: '',
  recencyTo: '',
  barriers: [],
  entryKeywords: [],
  readiness: [],
  facilityIds: [],
  offeringIds: [],
  facilityScopeToCustomer: false,
  radiusKm: '',
  activeConversationExclude: false,
  excludeSegmentIds: []
})

// Ordered stage values backing the Funnel Stage From/To range selector.
export const funnelStageValues = funnelStageOptions.map(o => o.value)

// Converts the From/To range selectors into the contiguous stage list the rest of the
// pipeline (backend map, description, tags) already understands. "To" is EXCLUSIVE — it names
// the stage the user must NOT have reached — so the included stages run from `from` up to, but
// not including, `to`.
export const funnelRangeToStages = (from, to) => {
  const fromIdx = (from === '' || from === null || from === undefined) ? 0 : funnelStageValues.indexOf(from)
  const toIdx = (to === '' || to === null || to === undefined || to === 'any') ? funnelStageValues.length : funnelStageValues.indexOf(to)
  if (fromIdx === -1 || toIdx === -1 || toIdx <= fromIdx) return []
  return funnelStageValues.slice(fromIdx, toIdx)
}

// Recovers a From/To range from a (possibly legacy, non-contiguous) saved stage list —
// takes the min/max index, which round-trips exactly for ranges produced by this UI. Since "To"
// is exclusive, it maps to the stage just after the furthest reached one (or "any" if none).
export const stagesToFunnelRange = (stagesList) => {
  const indices = (stagesList || []).map(s => funnelStageValues.indexOf(s)).filter(i => i !== -1)
  if (!indices.length) return { funnelFrom: '', funnelTo: 'any' }
  const minIdx = Math.min(...indices)
  const maxIdx = Math.max(...indices)
  return {
    funnelFrom: funnelStageValues[minIdx],
    funnelTo: maxIdx >= funnelStageValues.length - 1 ? 'any' : funnelStageValues[maxIdx + 1]
  }
}

// Human-readable sentence shown under the Funnel Stage range selector.
export const funnelRangeHint = (from, to) => {
  const fromIdx = (from === '' || from === null || from === undefined) ? 0 : funnelStageValues.indexOf(from)
  const fromLabel = (funnelStageOptions[fromIdx] || funnelStageOptions[0]).label
  if (to === '' || to === null || to === undefined || to === 'any') {
    return `Users who reached <strong>${fromLabel}</strong> or further — no upper limit on stage`
  }
  const toIdx = funnelStageValues.indexOf(to)
  const toLabel = (funnelStageOptions[toIdx] || funnelStageOptions[fromIdx]).label
  return `Users who reached <strong>${fromLabel}</strong> but have not reached <strong>${toLabel}</strong>`
}

export const slugify = (str) => {
  const slug = (str || '').toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  return slug || 'segment'
}

// PRD naming convention: [journey_id]_[segment_stage]_[number], e.g. mydawa_mid_journey_stall_002
export const generateSegmentName = (journey, conversationStageOption, existingNames) => {
  const journeySlug = slugify(journey)
  const stageSlug = slugify(conversationStageOption || 'general')
  const prefix = `${journeySlug}_${stageSlug}_`
  const usedNumbers = (existingNames || [])
    .filter(name => name && name.startsWith(prefix))
    .map(name => parseInt(name.slice(prefix.length), 10))
    .filter(n => !isNaN(n))
  const next = (usedNumbers.length ? Math.max(...usedNumbers) : 0) + 1
  return `${prefix}${String(next).padStart(3, '0')}`
}

const ageValid = (minAge, maxAge) => {
  if (minAge === '' || maxAge === '' || minAge === null || maxAge === null) return true
  return Number(minAge) < Number(maxAge)
}

const recencyValid = (recencyFrom, recencyTo) => {
  if (recencyFrom === '' || recencyTo === '' || recencyFrom === null || recencyTo === null) return true
  if (recencyTo === 'all_time') return true
  return Number(recencyFrom) < Number(recencyTo)
}

const funnelRangeValid = (funnelFrom, funnelTo) => {
  if (funnelTo === 'any' || funnelTo === '' || funnelTo === null || funnelTo === undefined) return true
  const fromIdx = funnelFrom === '' ? 0 : funnelStageValues.indexOf(funnelFrom)
  const toIdx = funnelStageValues.indexOf(funnelTo)
  // "To" is exclusive (the not-reached stage), so it must sit strictly after "From".
  return toIdx === -1 || fromIdx === -1 || toIdx > fromIdx
}

const stageEnteredValid = (stageEnteredFrom, stageEnteredTo) => {
  if (!stageEnteredFrom || !stageEnteredTo) return true
  return moment(stageEnteredFrom).isSameOrBefore(moment(stageEnteredTo))
}

// Any Proximity-to-Facility constraint (facility / offerings / client scope) only filters the
// audience together with a radius — without one the backend emits no proximity clause. So a radius is
// mandatory the moment any of those is set.
const proximityRadiusValid = (filters) => {
  const hasProximity = (filters.facilityIds && filters.facilityIds.length) ||
    (filters.offeringIds && filters.offeringIds.length) ||
    filters.facilityScopeToCustomer
  return !hasProximity || !!filters.radiusKm
}

export const validateFilters = (filters) => ({
  ageValid: ageValid(filters.minAge, filters.maxAge),
  recencyValid: recencyValid(filters.recencyFrom, filters.recencyTo),
  funnelRangeValid: funnelRangeValid(filters.funnelFrom, filters.funnelTo),
  stageEnteredValid: stageEnteredValid(filters.stageEnteredFrom, filters.stageEnteredTo),
  proximityRadiusValid: proximityRadiusValid(filters)
})

// Builds the payload for the existing `audience-builder/{env}/users/re-engagement-audience` count endpoint.
// Only FUNCTIONAL_FIELDS (see constants.js) are guaranteed to affect the returned count; the rest are
// passed through defensively in case the external service starts honoring them.
//
// The funnel stage is passed as a From/To RANGE (funnelStageFrom / funnelStageTo), NOT an expanded
// stage list — nivi-audience-manager turns it into a conversation_transition_logs (ctl) query:
// reached `from` (EXISTS on ctl app_name/stage) AND, unless `to` is empty, NOT reached `to`
// (NOT EXISTS). "To = any" means no upper bound, so funnelStageTo is sent empty.
// conversationStageOption/stateProvince/cityLga/excludeSegmentIds/facilityIds+radiusKm/
// stageEnteredFrom+To are all implemented server-side (nivi-audience-manager). `journey` and
// `stageEnteredKey` are sent explicitly so the backend can resolve the journey's real funnel-stage
// config (journey_settings). Barriers and readiness remain genuinely not-enforced.
export const buildCountPayload = (filters, countryName) => {
  const funnelStageFrom = filters.funnelFrom || funnelStageValues[0]
  const funnelStageTo = (filters.funnelTo && filters.funnelTo !== 'any') ? filters.funnelTo : ''
  const stageEnteredKey = funnelStageFrom

  const attrs = {
    countryId: filters.country_id,
    country_name: countryName,
    channels: filters.channels.join(),
    genders: filters.gender.join(),
    minAge: filters.minAge,
    maxAge: filters.maxAge,
    needSampleData: true,
    numberOfTranscripts: 0,
    journey: filters.journey,
    funnelStageFrom,
    funnelStageTo,
    conversationStageOption: filters.conversationStageOption,
    stateProvince: filters.stateProvince,
    cityLga: filters.cityLga,
    excludeSegmentIds: filters.excludeSegmentIds.join(),
    facilityIds: (filters.facilityIds || []).join(),
    // The three facility controls are independent proximity constraints (each narrows which
    // facilities the user must be near) — all enforced server-side against referral_resources /
    // referral_resources_to_offerings / clients (nivi-audience-manager). Offerings: "any of these".
    facilityOfferingIds: (filters.offeringIds || []).join(),
    // "Limit to customer's facilities": matched facilities must belong to that client
    // (referral_resources.client_id -> clients.name). Independent of picking a specific facility.
    facilityClientName: (filters.facilityScopeToCustomer && filters.customer) ? filters.customer : '',
    radiusKm: filters.radiusKm,
    activeConversationExclude: filters.activeConversationExclude,
    stageEnteredKey: (filters.stageEnteredFrom || filters.stageEnteredTo) ? stageEnteredKey : '',
    stageEnteredFrom: filters.stageEnteredFrom,
    stageEnteredTo: filters.stageEnteredTo,
    // TODO(nivi-audience-manager): no backend data model exists for readiness (no join path from
    // readiness_state_change to users) or for barriers — kept best-effort, same pattern as before.
    barriers: filters.barriers.join(),
    readiness: filters.readiness.join()
  }

  // Entry keywords reuse the same keywords/keywordsOp params the old audience builder already
  // proves are honored by the live count — "any of these keywords" maps to the IN operator (id 2).
  if (filters.entryKeywords.length) {
    attrs.keywords = filters.entryKeywords.join()
    attrs.keywordsOp = 2
  }

  // Recency ("inactive from X to Y days") is genuinely functional — it reuses the same
  // engagementStartDate/engagementEndDate params the count endpoint already accepts.
  if (filters.recencyTo && filters.recencyTo !== 'all_time') {
    attrs.engagementStartDate = moment.utc().subtract(Number(filters.recencyTo), 'days').startOf('day').utc(true)
  }
  if (filters.recencyFrom) {
    attrs.engagementEndDate = moment.utc().subtract(Number(filters.recencyFrom), 'days').endOf('day').utc(true)
  }

  if (filters.journey) {
    attrs.conversations = JSON.stringify([{ conversationOp: 4, conversation: filters.journey, stages: [] }])
  }

  // Real, functional: mirrors the old audience builder's active-conversation exclusion —
  // scoped to the currently selected journey since the AI builder only targets one at a time.
  if (filters.activeConversationExclude && filters.journey) {
    attrs.activeConversationsToExclude = filters.journey
  }

  return Object.entries(attrs).reduce((acc, [k, v]) => {
    if (v !== null && v !== '' && v !== undefined) acc[k] = v
    return acc
  }, {})
}

// Builds the `criteria` JSON persisted with the saved segment — additive to the existing schema,
// includes every field (functional or best-effort) so the segment definition is complete and reusable.
export const buildSaveCriteria = (filters, countPayload, savedUserCount) => ({
  ...countPayload,
  customer: filters.customer,
  journeyLabel: filters.journey,
  // The count payload only carries the derived engagement dates, so keep the day offsets to restore
  // the Inactivity window when the segment is reopened.
  recencyFrom: filters.recencyFrom || '',
  recencyTo: filters.recencyTo || '',
  // null = the count was never (re)computed for these filters; don't store a misleading 0.
  ...(savedUserCount !== null && savedUserCount !== undefined ? { savedUserCount } : {})
})

export const criteriaToFilters = (criteria) => {
  return {
    ...defaultFilters(),
    country: criteria.country_name || null,
    country_id: criteria.countryId || null,
    customer: criteria.customer || null,
    journey: criteria.journeyLabel || null,
    channels: criteria.channels ? criteria.channels.split(',').filter(Boolean) : [],
    gender: criteria.genders ? criteria.genders.split(',').filter(Boolean) : [],
    minAge: criteria.minAge || '',
    maxAge: criteria.maxAge || '',
    // Funnel is now stored as a From/To range (funnelStageFrom / funnelStageTo, To empty = "any").
    // The first stage is the select's blank option (value ''), so map it back.
    funnelFrom: (criteria.funnelStageFrom && criteria.funnelStageFrom !== funnelStageValues[0]) ? criteria.funnelStageFrom : '',
    funnelTo: criteria.funnelStageTo || 'any',
    conversationStageOption: criteria.conversationStageOption || null,
    stateProvince: criteria.stateProvince || null,
    cityLga: criteria.cityLga || null,
    barriers: criteria.barriers ? criteria.barriers.split(',').filter(Boolean) : [],
    entryKeywords: criteria.keywords ? criteria.keywords.split(',').filter(Boolean) : [],
    readiness: criteria.readiness ? criteria.readiness.split(',').filter(Boolean) : [],
    facilityIds: criteria.facilityIds
      ? criteria.facilityIds.split(',').filter(Boolean).map(id => (isNaN(id) ? id : Number(id)))
      : [],
    offeringIds: criteria.facilityOfferingIds
      ? criteria.facilityOfferingIds.split(',').filter(Boolean).map(id => (isNaN(id) ? id : Number(id)))
      : [],
    facilityScopeToCustomer: !!criteria.facilityClientName,
    radiusKm: criteria.radiusKm || '',
    activeConversationExclude: !!criteria.activeConversationExclude,
    recencyFrom: criteria.recencyFrom || '',
    recencyTo: criteria.recencyTo || '',
    stageEnteredFrom: criteria.stageEnteredFrom || '',
    stageEnteredTo: criteria.stageEnteredTo || '',
    excludeSegmentIds: criteria.excludeSegmentIds
      ? criteria.excludeSegmentIds.split(',').filter(Boolean).map(id => (isNaN(id) ? id : Number(id)))
      : []
  }
}

const labelFor = (options, value, key = 'value') => {
  const found = options.find(o => o[key] === value)
  return found ? found.label : value
}

// Criteria fields can be stored either as comma strings (AI builder) or arrays (legacy/other
// builders) — coerce to a clean array so the card renderers never assume one shape.
const toList = (v) => {
  if (Array.isArray(v)) return v.filter(Boolean)
  if (typeof v === 'string') return v.split(',').filter(Boolean)
  return []
}

// Short human-readable summary shown on a segment card.
export const buildCriteriaDescription = (criteria) => {
  const parts = []
  if (criteria.journeyLabel) parts.push(criteria.journeyLabel)
  if (toList(criteria.channels).length) parts.push(toList(criteria.channels).join('/'))
  if (toList(criteria.genders).length) parts.push(toList(criteria.genders).join('/'))
  if (criteria.minAge || criteria.maxAge) parts.push(`Age ${criteria.minAge || 'Any'}-${criteria.maxAge || 'Any'}`)
  if ((criteria.funnelStageFrom && criteria.funnelStageFrom !== funnelStageValues[0]) || criteria.funnelStageTo) {
    const fromLabel = labelFor(funnelStageOptions, criteria.funnelStageFrom || funnelStageValues[0])
    parts.push(criteria.funnelStageTo ? `${fromLabel} → ${labelFor(funnelStageOptions, criteria.funnelStageTo)}` : `${fromLabel}+`)
  }
  if (criteria.conversationStageOption) {
    // Stored as "conversation::stage" — show just the stage part, it's already human-readable.
    const stagePart = criteria.conversationStageOption.includes('::') ? criteria.conversationStageOption.split('::')[1] : criteria.conversationStageOption
    parts.push(`Stage: ${stagePart}`)
  }
  if (toList(criteria.keywords).length) parts.push(`Keywords: ${toList(criteria.keywords).join(', ')}`)
  if (toList(criteria.readiness).length) parts.push(`Readiness: ${toList(criteria.readiness).join('/')}`)
  return parts.length ? parts.join(' · ') : 'No filters set'
}

export const buildCriteriaTags = (criteria) => {
  const tags = []
  tags.push(...toList(criteria.barriers).map(b => labelFor(barrierOptions, b)))
  if (criteria.funnelStageFrom && criteria.funnelStageFrom !== funnelStageValues[0]) tags.push(labelFor(funnelStageOptions, criteria.funnelStageFrom))
  if (criteria.funnelStageTo) tags.push(`not ${labelFor(funnelStageOptions, criteria.funnelStageTo)}`)
  tags.push(...toList(criteria.readiness))
  return tags.filter(Boolean)
}



// WEBPACK FOOTER //
// ./src/components/audience-builder/ai-audience/helpers.js